/**
 * Field schemas per CNH template id.
 *
 * The SERPRO/VIO QR carries fields as a positional `^`-separated list; the
 * meaning of each position depends on the document's `templateId`. The 2022 CNH
 * (template 83) inserts a `nome_civil` field at index 1, shifting everything
 * after it relative to the older templates 2/4. Getting this wrong silently
 * mis-labels every field, so the mapping is driven by `templateId`.
 *
 * Field names and order come from the VIO app's `assets/json/templates.json`.
 */

/** CNHData keys, in the field order of CNH templates 2 and 4 (18 fields). */
const SCHEMA_CNH_18: string[] = [
  "nome", "identidade", "cpf", "dataNascimento", "filiacao1", "filiacao2",
  "permissao", "acc", "categoria", "registro", "validade", "primeiraHabilitacao",
  "observacoes", "local", "uf", "dataEmissao", "codigoLocal", "renach",
];

/** CNHData keys, in the field order of CNH template 83 (19 fields; adds nomeCompleto). */
const SCHEMA_CNH_19: string[] = [
  "nome", "nomeCompleto", "identidade", "cpf", "dataNascimento", "filiacao1", "filiacao2",
  "permissao", "acc", "categoria", "registro", "validade", "primeiraHabilitacao",
  "observacoes", "local", "uf", "dataEmissao", "codigoLocal", "renach",
];

/** templateId → positional field schema. */
const SCHEMAS: Record<number, string[]> = {
  2: SCHEMA_CNH_18,
  4: SCHEMA_CNH_18,
  83: SCHEMA_CNH_19,
};

/** Template ids this library maps to named CNH fields. */
export const CNH_TEMPLATE_IDS: number[] = Object.keys(SCHEMAS).map(Number);

/** Every CNHData field key a schema can populate. */
const ALL_KEYS = [
  "nome", "nomeCompleto", "identidade", "cpf", "dataNascimento", "filiacao1",
  "filiacao2", "permissao", "acc", "observacoes", "categoria", "registro",
  "validade", "primeiraHabilitacao", "local", "uf", "dataEmissao", "codigoLocal",
  "renach",
] as const;

/** Named CNH fields produced by {@link mapCnhFields}; every key is always a string. */
export type CnhNamedFields = Record<(typeof ALL_KEYS)[number], string>;

/**
 * Maps a positional field list to named CNH fields using the schema for
 * {@link templateId}. Unknown templates yield all-empty named fields (callers
 * still get the raw positional `fields[]`). Every known key is always present
 * (empty string when absent), so the result is safe to spread into `CNHData`.
 */
export function mapCnhFields(templateId: number, fields: string[]): CnhNamedFields {
  const out = {} as CnhNamedFields;
  for (const key of ALL_KEYS) out[key] = "";

  const schema = SCHEMAS[templateId];
  if (!schema) return out;

  schema.forEach((key, i) => {
    out[key as keyof CnhNamedFields] = fields[i]?.trim() ?? "";
  });
  return out;
}
